import { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../context/auth-context';
import LocationsAPI from '../../services/api/admin/locations-api';
import EmployeesAPI from '../../services/api/admin/employees-api';
import TimecardsAPI from '../../services/api/timecards/timecards-api';
import TimecardApprovalsAPI from '../../services/api/timecards/timecard-approvals-api';
import Logger from '../../utils/logger';
import { format, previousMonday } from 'date-fns';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridToolbar,
  gridSortedRowIdsSelector,
} from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import WarningIcon from '@mui/icons-material/Warning';
import { TimecardConfirmationModal } from './TimecardConfirmationModal';
import { TimecardUnapprovalModal } from './TimecardUnapprovalModal';
import { TimecardAdminApprovalModal } from './TimecardAdminApprovalModal';

// this selects all unfiltered rows for exporting.
const getFilteredRows = ({ apiRef }) => {
  let exportedRows = gridSortedRowIdsSelector(apiRef);
  exportedRows = exportedRows.filter((id) => {
    // Keep the existing Jeremy Price exclusion (name-based).
    if (typeof id === 'string' && id.includes('Price, Jeremy')) return false;
    // Exclude shop-synced time STRUCTURALLY (not by name): it's shown on-screen
    // for PM visibility but excluded from the accounting CSV, since FabShopTools'
    // own export already covers it. See Shop Time Bridge (#243).
    const row = apiRef?.current?.getRow?.(id);
    if (row?.is_shop_synced) return false;
    return true;
  });
  Logger.debug('Filtered rows for export', { count: exportedRows?.length });
  return exportedRows;
};

// Calculate the relevant Sunday for the week ending date picker
// If today is Monday: Select the PREVIOUS Sunday (yesterday)
// If today is Tuesday through Sunday: Select the NEXT Sunday
const getRelevantSunday = (date = new Date()) => {
  const result = new Date(date);
  const currentDay = result.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.

  if (currentDay === 1) {
    // It's Monday - go back to yesterday (previous Sunday)
    result.setDate(result.getDate() - 1);
  } else {
    // Tuesday through Sunday - go forward to next Sunday
    const daysUntilSunday = currentDay === 0 ? 7 : 7 - currentDay;
    result.setDate(result.getDate() + daysUntilSunday);
  }

  return result;
};

export const TimecardDownload = () => {
  const auth = useContext(AuthContext);

  const [weekTimecards, setWeekTimecards] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [packageSummary, setPackageSummary] = useState([]);
  // Initialize with the relevant Sunday based on current day of week
  const [weekEnd, setWeekEnd] = useState(() => {
    const relevantSunday = getRelevantSunday();
    return format(relevantSunday, 'yyyy/MM/dd');
  });
  const [summaryType, setSummaryType] = useState('timecard');
  const [dataRows, setDataRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [foremen, setForemen] = useState([]);
  const [selectedForeman, setSelectedForeman] = useState('all');
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('active');
  const [modalOpen, setModalOpen] = useState(false);
  const [unapprovalModalOpen, setUnapprovalModalOpen] = useState(false);
  const [adminApprovalModalOpen, setAdminApprovalModalOpen] = useState(false);
  const [adminUnapprovalModalOpen, setAdminUnapprovalModalOpen] =
    useState(false);
  const [isWeekApproved, setIsWeekApproved] = useState(false);
  const [isTimecardUser, setIsTimecardUser] = useState(false);
  const [allApprovals, setAllApprovals] = useState([]);
  const [users, setUsers] = useState([]); // For looking up approver names

  // this selects only the sunday dates so they only appear in the calendar
  const isSunday = (date) => {
    const day = date.getDay();
    return day !== 0;
  };

  const weekEndingDate = () => {
    return weekEnd;
  };

  const isRegularTimecardUser = auth.isTimecard && !auth.isAdmin;

  // Load initial timecard data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      if (!weekEnd || !auth.token) return;

      const weekStart = format(previousMonday(new Date(weekEnd)), 'yyyy/MM/dd');

      try {
        const responseData = await TimecardsAPI.getTimecardsWeek(
          {
            date_beg: weekStart,
            date_end: weekEnd,
            // Regular foremen load their own data first (getForemen will set selectedForeman to self)
            foremanId: isRegularTimecardUser ? auth.userId : undefined,
          },
          auth.token
        );
        Logger.debug('Initial timecard data loaded', {
          weekTimecardsCount: responseData.time_week?.length,
        });
        setWeekTimecards(responseData.time_week);
        setDailySummary(responseData.time_summary);
        setPackageSummary(responseData.time_week_sum_pack);
      } catch (err) {
        Logger.error('Error loading initial timecard data', err);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token]); // Only run on mount when auth.token becomes available

  // Fetch locations data
  useEffect(() => {
    const getLocations = async () => {
      try {
        const locationsData = await LocationsAPI.getAll(auth.token);
        setLocations(locationsData);
      } catch (err) {
        Logger.error('Error getting the locations', err);
      }
    };
    if (auth.token) {
      getLocations();
    }
  }, [auth.token]);

  // Fetch foremen data and check if current user is a foreman
  useEffect(() => {
    const getForemen = async () => {
      try {
        const allUsers = await EmployeesAPI.getAll(auth.token);
        setUsers(allUsers); // Store all users for approver name lookup

        const currentUser = allUsers.find((user) => user.id === auth.userId);
        setIsTimecardUser(currentUser?.is_timecard === true);

        const foremenList = allUsers.filter(
          (user) => user.is_timecard === true
        );
        setForemen(foremenList);

        if (auth.isTimecard && currentUser) {
          setSelectedForeman(currentUser.id);
        }
      } catch (err) {
        Logger.error('Error getting the foremen', err);
      }
    };
    if (auth.token) {
      getForemen();
    }
  }, [auth.token, auth.userId, auth.isTimecard, auth.isAdmin]);

  // Check approval status when weekEnd or selectedForeman changes
  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (!weekEnd) {
        setAllApprovals([]);
        setIsWeekApproved(false);
        return;
      }

      try {
        // Get all approvals for the week to display badges even when no foreman is selected
        const allApprovals = await TimecardApprovalsAPI.getWeekApprovals(
          weekEnd.replace(/\//g, '-'),
          auth.token
        );
        setAllApprovals(allApprovals);

        if (selectedForeman !== 'all') {
          const status = await TimecardApprovalsAPI.getApprovalStatus(
            selectedForeman,
            weekEnd.replace(/\//g, '-'),
            auth.token
          );

          if (status) {
            setIsWeekApproved(true);
          } else {
            setIsWeekApproved(false);
          }
        } else {
          setIsWeekApproved(false);
        }
      } catch (err) {
        Logger.error('Error checking approval status', err);
      }
    };

    checkApprovalStatus();
  }, [weekEnd, selectedForeman, auth.token, users]);

  // Refetch timecard data when selectedForeman changes (for admins viewing different foremen)
  useEffect(() => {
    // Skip if weekEnd is not set yet (initial load will handle this)
    if (!weekEnd) return;

    // Fetch new timecard data for the selected foreman
    const fetchTimecardsForForeman = async () => {
      const weekStart = format(previousMonday(new Date(weekEnd)), 'yyyy/MM/dd');

      try {
        const responseData = await TimecardsAPI.getTimecardsWeek(
          {
            date_beg: weekStart,
            date_end: weekEnd,
            foremanId: selectedForeman !== 'all' ? selectedForeman : undefined,
            locationId:
              selectedLocation !== 'all' ? selectedLocation : undefined,
          },
          auth.token
        );
        setWeekTimecards(responseData.time_week);
        setDailySummary(responseData.time_summary);
        setPackageSummary(responseData.time_week_sum_pack);
      } catch (err) {
        Logger.error('Error fetching timecards for foreman', err);
      }
    };

    fetchTimecardsForForeman();
  }, [selectedForeman, weekEnd, selectedLocation, auth.token]);

  // Filter data based on selected location, foreman, and employment status
  const filterDataByLocationAndForeman = useCallback(
    (data) => {
      let filtered = data;

      if (selectedLocation !== 'all') {
        filtered = filtered.filter(
          (row) => row.location_id === selectedLocation
        );
      }

      if (selectedForeman !== 'all') {
        filtered = filtered.filter((row) => row.foreman_id === selectedForeman);
      }

      // Filter by employment status: active = only active employees, inactive = all other statuses
      if (employeeStatusFilter === 'active') {
        filtered = filtered.filter(
          (row) => row.employment_status_id === 'active'
        );
      } else {
        filtered = filtered.filter(
          (row) => row.employment_status_id !== 'active'
        );
      }

      return filtered;
    },
    [selectedLocation, selectedForeman, employeeStatusFilter]
  );

  useEffect(() => {
    let filteredData;
    if (summaryType === 'timecard') {
      filteredData = filterDataByLocationAndForeman(weekTimecards);
      setDataRows(filteredData);
    } else if (summaryType === 'dailySummary') {
      filteredData = filterDataByLocationAndForeman(dailySummary);
      setDataRows(filteredData);
    } else if (summaryType === 'packSummary') {
      filteredData = filterDataByLocationAndForeman(packageSummary);
      setDataRows(filteredData);
    }
  }, [
    summaryType,
    dailySummary,
    weekTimecards,
    packageSummary,
    selectedLocation,
    selectedForeman,
    employeeStatusFilter,
    filterDataByLocationAndForeman,
  ]);

  const columns = [
    {
      field: 'status',
      headerName: 'Approved',
      width: 90,
      editable: false,
      renderCell: (params) => {
        // Check if this specific row/entry is locked (not just if an approval exists)
        const isLocked = params.row.is_locked;

        if (!isLocked) {
          return (
            <Tooltip title="Not yet approved" arrow>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </Tooltip>
          );
        }

        // Entry is locked - show approval badge
        // Try to get lock date from the row, or fall back to approval record
        const lockedAt = params.row.locked_at;
        const foremanId = params.row.foreman_id;
        const approval =
          allApprovals && allApprovals.find((a) => a.foreman_id === foremanId);

        // Use locked_at from row if available, otherwise fall back to approval date
        const approvalDateObj = lockedAt
          ? new Date(lockedAt)
          : approval
            ? new Date(approval.approved_at)
            : new Date();

        const formattedDate = approvalDateObj.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
        const formattedTime = approvalDateObj.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });

        // Look up approver name from approval record
        let approverName = null;
        if (approval) {
          const approverId = approval.approved_by_user_id;
          const approver = users.find((u) => u.id === approverId);
          approverName = approver
            ? `${approver.firstname} ${approver.lastname}`
            : null;
        }

        const tooltipText = approverName
          ? `Approved by ${approverName} on ${formattedDate} at ${formattedTime}`
          : `Approved on ${formattedDate} at ${formattedTime}`;

        return (
          <Tooltip title={tooltipText} arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckIcon
                  sx={{
                    fontSize: 16,
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'weekEnding',
      headerName: 'Week Ending',
      width: 95,
      valueGetter: weekEndingDate,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 84,
      editable: false,
      renderCell: (params) => {
        const value = params.value || '';
        return (
          <Tooltip title={value} arrow>
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'foreman_name',
      headerName: 'Foreman',
      width: 113,
      editable: false,
      sortable: true,
      valueGetter: (params) => {
        const value = params.value;
        if (!value || value === null || value === undefined) {
          return 'No Foreman';
        }
        // Handle case where value might be an object
        if (typeof value === 'object') {
          // If it's an object with name properties, format as firstname lastname
          if (value.lastname && value.firstname) {
            return `${value.firstname} ${value.lastname}`;
          }
          // Otherwise return a string representation
          return String(value);
        }
        // If it's a string in "lastname, firstname" format, convert to "firstname lastname"
        if (typeof value === 'string' && value.includes(', ')) {
          const parts = value.split(', ');
          if (parts.length === 2) {
            return `${parts[1]} ${parts[0]}`;
          }
        }
        return value;
      },
    },
    {
      field: 'full_name',
      headerName: 'Employee Name',
      width: 140,
      editable: false,
    },
    {
      field: 'union',
      headerName: 'Union',
      width: 60,
      editable: false,
      valueGetter: (params) => {
        return params.value || '';
      },
    },
    {
      field: 'exception',
      headerName: 'Exception',
      width: 113,
      editable: false,
    },
    {
      field: 'last_status_change_date',
      headerName: 'Layoff Date',
      width: 120,
      editable: false,
      valueGetter: (params) => {
        const date = params.row.last_status_change_date;
        if (!date) return '';
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      },
    },
    {
      field: 'coins_empl',
      headerName: 'COINS Employee #',
      width: 20,
      editable: false,
    },
    {
      field: 'project_name',
      headerName: 'Project',
      width: 210,
      editable: false,
      renderCell: (params) => {
        const value = params.value || '';
        return (
          <Tooltip title={value} arrow>
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'project_number',
      headerName: 'Proj #',
      width: 95,
      editable: false,
    },
    {
      field: 'package_name',
      headerName: 'Package',
      width: 200,
      editable: false,
      valueGetter: (params) => {
        const row = params.row;
        if (row.autodesk_name?.trim()) {
          return `${row.autodesk_name} - ${row.package_name}`;
        }
        return row.package_name || '';
      },
    },
    {
      field: 'activity_name',
      headerName: 'Activity',
      width: 110,
      editable: false,
    },
    {
      field: 'location_type',
      headerName: 'Type',
      width: 95,
      editable: false,
      renderCell: (params) => {
        const locationType = params.value;
        if (!locationType) {
          return null;
        }
        const isShop = locationType.toLowerCase() === 'shop';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={isShop ? 'SHOP' : 'FIELD'}
              size="small"
              color={isShop ? 'warning' : 'primary'}
              sx={{
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            {params.row.is_location_override && (
              <Tooltip title="Shop-to-field location override">
                <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'phase',
      headerName: 'Ph',
      width: 10,
      editable: false,
    },
    {
      field: 'cost_code',
      headerName: 'CC',
      width: 70,
      editable: false,
    },
    {
      field: 'shift',
      headerName: 'Shift',
      width: 54,
      editable: false,
    },
    {
      field: 'straight_time',
      headerName: 'STRAIGHT-TIME',
      width: 36,
      editable: false,
    },
    {
      field: 'st',
      headerName: 'TOTAL',
      width: 54,
      editable: false,
    },
    {
      field: 'MON_ST',
      headerName: 'MON',
      width: 36,
      editable: false,
    },
    {
      field: 'TUE_ST',
      headerName: 'TUE',
      width: 36,
      editable: false,
    },
    {
      field: 'WED_ST',
      headerName: 'WED',
      width: 36,
      editable: false,
    },
    {
      field: 'THU_ST',
      headerName: 'THU',
      width: 36,
      editable: false,
    },
    {
      field: 'FRI_ST',
      headerName: 'FRI',
      width: 36,
      editable: false,
    },
    {
      field: 'SAT_ST',
      headerName: 'SAT',
      width: 36,
      editable: false,
    },
    {
      field: 'SUN_ST',
      headerName: 'SUN',
      width: 36,
      editable: false,
    },
    {
      field: 'overtime',
      headerName: 'OVERTIME',
      width: 36,
      editable: false,
    },
    {
      field: 'ot',
      headerName: 'TOTAL',
      width: 54,
      editable: false,
    },
    {
      field: 'MON_OT',
      headerName: 'MON',
      width: 36,
      editable: false,
    },
    {
      field: 'TUE_OT',
      headerName: 'TUE',
      width: 36,
      editable: false,
    },
    {
      field: 'WED_OT',
      headerName: 'WED',
      width: 36,
      editable: false,
    },
    {
      field: 'THU_OT',
      headerName: 'THU',
      width: 36,
      editable: false,
    },
    {
      field: 'FRI_OT',
      headerName: 'FRI',
      width: 36,
      editable: false,
    },
    {
      field: 'SAT_OT',
      headerName: 'SAT',
      width: 36,
      editable: false,
    },
    {
      field: 'SUN_OT',
      headerName: 'SUN',
      width: 36,
      editable: false,
    },
    {
      field: 'doubletime',
      headerName: 'DOUBLETIME',
      width: 36,
      editable: false,
    },
    {
      field: 'dt',
      headerName: 'TOTAL',
      width: 54,
      editable: false,
    },
    {
      field: 'MON_DT',
      headerName: 'MON',
      width: 36,
      editable: false,
    },
    {
      field: 'TUE_DT',
      headerName: 'TUE',
      width: 36,
      editable: false,
    },
    {
      field: 'WED_DT',
      headerName: 'WED',
      width: 36,
      editable: false,
    },
    {
      field: 'THU_DT',
      headerName: 'THU',
      width: 36,
      editable: false,
    },
    {
      field: 'FRI_DT',
      headerName: 'FRI',
      width: 36,
      editable: false,
    },
    {
      field: 'SAT_DT',
      headerName: 'SAT',
      width: 36,
      editable: false,
    },
    {
      field: 'SUN_DT',
      headerName: 'SUN',
      width: 36,
      editable: false,
    },
    {
      field: 'notes',
      headerName: 'NOTES',
      width: 100,
      editable: false,
      renderCell: (params) => {
        const value = params.value || '';
        return (
          <Tooltip title={value} arrow>
            <div
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  // Calculate summary data for the confirmation modal
  const calculateSummary = () => {
    const totalEntries = dataRows.length;
    let straightTime = 0;
    let overtime = 0;
    let doubleTime = 0;

    dataRows.forEach((row) => {
      straightTime += parseFloat(row.st || 0);
      overtime += parseFloat(row.ot || 0);
      doubleTime += parseFloat(row.dt || 0);
    });

    const totalHours = straightTime + overtime + doubleTime;

    return {
      totalHours,
      totalEntries,
      straightTime,
      overtime,
      doubleTime,
    };
  };

  const handleApproveWeekClick = () => {
    if (selectedForeman === 'all') {
      alert('Please select a specific foreman to approve.');
      return;
    }
    if (isWeekApproved) {
      setUnapprovalModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleUnapprovalModalClose = () => {
    setUnapprovalModalOpen(false);
  };

  const handleApproveWeek = async () => {
    try {
      await TimecardApprovalsAPI.approveWeek(
        selectedForeman,
        weekEnd.replace(/\//g, '-'),
        auth.token
      );
      setIsWeekApproved(true);
      setModalOpen(false);

      // Update allApprovals state to include the new approval
      setAllApprovals((prev) => [
        ...prev,
        {
          foreman_id: selectedForeman,
          week_ending_date: weekEnd,
          approved_at: new Date().toISOString(),
          approved_by_user_id: auth.userId,
          is_active: true,
        },
      ]);

      // Refresh timecard data to get updated is_locked status
      await refreshTimecardsData();
    } catch (err) {
      Logger.error('Error approving week', err);
      alert('Failed to approve week. Please try again.');
    }
  };

  const handleUnapproveWeek = async (reason) => {
    try {
      await TimecardApprovalsAPI.unapproveWeek(
        selectedForeman,
        weekEnd.replace(/\//g, '-'),
        auth.token
      );
      setIsWeekApproved(false);
      setUnapprovalModalOpen(false);

      // Update allApprovals state to remove the approval
      setAllApprovals((prev) =>
        prev.filter((a) => a.foreman_id !== selectedForeman)
      );

      // Refresh timecard data to get updated is_locked status
      await refreshTimecardsData();
    } catch (err) {
      Logger.error('Error unapproving week', err);
      alert('Failed to unapprove week. Please try again.');
    }
  };

  // Admin approval on behalf handlers
  const handleAdminApprovalModalOpen = () => {
    if (selectedForeman === 'all') {
      alert('Please select a specific foreman to approve on behalf.');
      return;
    }
    setAdminApprovalModalOpen(true);
  };

  const handleAdminApprovalModalClose = () => {
    setAdminApprovalModalOpen(false);
  };

  const handleAdminApproveOnBehalf = async (reason) => {
    try {
      await TimecardApprovalsAPI.adminApprove(
        selectedForeman,
        weekEnd.replace(/\//g, '-'),
        reason,
        auth.token
      );
      setIsWeekApproved(true);
      setAdminApprovalModalOpen(false);

      // Update allApprovals state to include the new approval
      setAllApprovals((prev) => [
        ...prev,
        {
          foreman_id: selectedForeman,
          week_ending_date: weekEnd,
          approved_at: new Date().toISOString(),
          approved_by_user_id: auth.userId,
          is_active: true,
        },
      ]);

      // Refresh timecard data to get updated is_locked status
      await refreshTimecardsData();
    } catch (err) {
      Logger.error('Error approving on behalf', err);
      const errorMessage =
        err.response?.data?.message ||
        'Failed to approve on behalf. Please try again.';
      alert(errorMessage);
    }
  };

  // Admin unapproval modal handlers
  const handleAdminUnapprovalModalOpen = () => {
    if (selectedForeman === 'all') {
      alert('Please select a specific foreman to unapprove on behalf.');
      return;
    }
    setAdminUnapprovalModalOpen(true);
  };

  const handleAdminUnapprovalModalClose = () => {
    setAdminUnapprovalModalOpen(false);
  };

  const handleAdminUnapproveOnBehalf = async (reason) => {
    try {
      await TimecardApprovalsAPI.unapproveWeek(
        selectedForeman,
        weekEnd.replace(/\//g, '-'),
        auth.token
      );
      setIsWeekApproved(false);
      setAdminUnapprovalModalOpen(false);

      // Update allApprovals state to remove the approval
      setAllApprovals((prev) =>
        prev.filter((a) => a.foreman_id !== selectedForeman)
      );

      // Refresh timecard data to get updated is_locked status
      await refreshTimecardsData();
    } catch (err) {
      Logger.error('Error unapproving on behalf', err);
      const errorMessage =
        err.response?.data?.message ||
        'Failed to unapprove on behalf. Please try again.';
      alert(errorMessage);
    }
  };

  // Get the selected foreman's name for the admin approval modal
  const getSelectedForemanName = () => {
    if (selectedForeman === 'all') return '';
    const foreman = foremen.find((f) => f.id === selectedForeman);
    return foreman ? `${foreman.firstname} ${foreman.lastname}` : '';
  };

  // Helper function to refresh timecard data (used after approval/unapproval)
  const refreshTimecardsData = async () => {
    if (!weekEnd) return;

    const weekStart = format(previousMonday(new Date(weekEnd)), 'yyyy/MM/dd');

    try {
      const responseData = await TimecardsAPI.getTimecardsWeek(
        {
          date_beg: weekStart,
          date_end: weekEnd,
          foremanId: selectedForeman !== 'all' ? selectedForeman : undefined,
          locationId: selectedLocation !== 'all' ? selectedLocation : undefined,
        },
        auth.token
      );
      setWeekTimecards(responseData.time_week);
      setDailySummary(responseData.time_summary);
      setPackageSummary(responseData.time_week_sum_pack);
    } catch (err) {
      Logger.error('Error refreshing timecard data', err);
    }
  };

  // this function sets the week ending date and gets the timecards for that week
  const getTimecardsWeek = async (newValue) => {
    const selectedWeek = format(newValue, 'yyyy/MM/dd');

    setWeekEnd(selectedWeek);
    setIsWeekApproved(false); // Reset approval state when selecting a new week

    const weekStart = format(previousMonday(newValue), 'yyyy/MM/dd');

    try {
      const responseData = await TimecardsAPI.getTimecardsWeek(
        {
          date_beg: weekStart,
          date_end: selectedWeek,
          foremanId: selectedForeman !== 'all' ? selectedForeman : undefined,
          locationId: selectedLocation !== 'all' ? selectedLocation : undefined,
        },
        auth.token
      );
      Logger.debug('Timecard data retrieved', {
        weekTimecardsCount: responseData.time_week?.length,
      });
      setWeekTimecards(responseData.time_week);
      setDailySummary(responseData.time_summary);
      setPackageSummary(responseData.time_week_sum_pack);
    } catch (err) {
      Logger.error('Error getting the time for the week', err);
      alert(err.message);
    }
  };

  const columnGroupingModel = [
    {
      groupId: 'Straight-Time',
      children: [
        { field: 'st' },
        { field: 'MON_ST' },
        { field: 'TUE_ST' },
        { field: 'WED_ST' },
        { field: 'THU_ST' },
        { field: 'FRI_ST' },
        { field: 'SAT_ST' },
        { field: 'SUN_ST' },
      ],
    },
    {
      groupId: 'Overtime',
      children: [
        { field: 'ot' },
        { field: 'MON_OT' },
        { field: 'TUE_OT' },
        { field: 'WED_OT' },
        { field: 'THU_OT' },
        { field: 'FRI_OT' },
        { field: 'SAT_OT' },
        { field: 'SUN_OT' },
      ],
    },
    {
      groupId: 'Doubletime',
      children: [
        { field: 'dt' },
        { field: 'MON_DT' },
        { field: 'TUE_DT' },
        { field: 'WED_DT' },
        { field: 'THU_DT' },
        { field: 'FRI_DT' },
        { field: 'SAT_DT' },
        { field: 'SUN_DT' },
      ],
    },
  ];

  return (
    <Box sx={{ width: '95%', height: '90%', padding: 2 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 60,
          paddingTop: 2,
          paddingBottom: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <LocalizationProvider name="timeDate" dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              label="Week Ending"
              inputFormat="yyyy/MM/dd"
              value={weekEnd}
              onChange={getTimecardsWeek}
              // Fixed width: unsized, the field follows the font (~20 characters).
              // Narrower below md (tablets) so the toolbar fits on one row.
              renderInput={(params) => (
                <TextField {...params} sx={{ width: { xs: 145, md: 180 } }} />
              )}
              shouldDisableDate={isSunday}
            />
          </LocalizationProvider>
          <FormControl sx={{ minWidth: { xs: 100, md: 120 } }}>
            <InputLabel id="timecard_display">Display</InputLabel>
            <Select
              labelId="timecard_display"
              id="summary_type"
              value={summaryType}
              name="summary_type"
              label="Summary Type"
              onChange={(e) => {
                setSummaryType(e.target.value);
              }}
              sx={{
                height: 56,
                width: { xs: 100, md: 'auto' },
                minWidth: { xs: 100, md: 140 },
                maxWidth: 200,
              }}
            >
              <MenuItem key="timecard" value="timecard">
                Timecard
              </MenuItem>
              <MenuItem key="dailySummary" value="dailySummary">
                Daily Summary
              </MenuItem>
              <MenuItem key="packSummary" value="packSummary">
                Package Summary
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: 105, md: 120 } }}>
            <InputLabel id="location_filter">Location</InputLabel>
            <Select
              labelId="location_filter"
              id="selected_location"
              value={selectedLocation}
              name="selected_location"
              label="Location"
              onChange={(e) => {
                setSelectedLocation(e.target.value);
              }}
              sx={{
                height: 56,
                width: { xs: 105, md: 'auto' },
                minWidth: { xs: 105, md: 140 },
                maxWidth: 200,
              }}
            >
              <MenuItem key="all" value="all">
                All Locations
              </MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: { xs: 110, md: 120 } }}>
            <InputLabel id="foreman_filter">Foreman</InputLabel>
            <Select
              labelId="foreman_filter"
              id="selected_foreman"
              value={selectedForeman}
              name="selected_foreman"
              label="Foreman"
              onChange={(e) => {
                setSelectedForeman(e.target.value);
              }}
              sx={{
                height: 56,
                width: { xs: 110, md: 'auto' },
                minWidth: { xs: 110, md: 140 },
                maxWidth: 200,
              }}
            >
              {/* Show 'All Foremen' option for admins and foremen */}
              {(auth.isAdmin || auth.isTimecard) && (
                <MenuItem key="all" value="all">
                  All Foremen
                </MenuItem>
              )}
              {foremen.map((foreman) => (
                <MenuItem key={foreman.id} value={foreman.id}>
                  {foreman.firstname} {foreman.lastname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {weekEnd &&
            (auth.isAdmin || isTimecardUser) &&
            selectedForeman !== 'all' && (
              <>
                {auth.isTimecard && selectedForeman === auth.userId && (
                  <Button
                    variant="contained"
                    color={isWeekApproved ? 'success' : 'primary'}
                    startIcon={isWeekApproved ? <CheckCircleIcon /> : null}
                    onClick={handleApproveWeekClick}
                    sx={{
                      height: 56,
                      minWidth: { xs: 130, md: 140 },
                      px: { xs: 1.5, md: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isWeekApproved ? 'Approved' : 'Approve Week'}
                  </Button>
                )}
                {/* Admin Approve/Unapprove on Behalf button:
                    - Admin-Only: Always show when viewing any foreman
                    - Admin-Foremen: Only show when viewing ANOTHER foreman's data
                    - Regular foremen: Never show
                */}
                {auth.isAdmin &&
                  (!auth.isTimecard || selectedForeman !== auth.userId) && (
                    <Tooltip
                      title={
                        isWeekApproved
                          ? `Click to unapprove on behalf of ${getSelectedForemanName()}`
                          : `Approve on behalf of ${getSelectedForemanName()}`
                      }
                    >
                      <Button
                        variant={isWeekApproved ? 'contained' : 'outlined'}
                        color={isWeekApproved ? 'success' : 'secondary'}
                        startIcon={
                          isWeekApproved ? (
                            <CheckCircleIcon />
                          ) : (
                            <SupervisorAccountIcon />
                          )
                        }
                        onClick={
                          isWeekApproved
                            ? handleAdminUnapprovalModalOpen
                            : handleAdminApprovalModalOpen
                        }
                        sx={{ height: 56, whiteSpace: 'nowrap' }}
                      >
                        {isWeekApproved
                          ? 'Approved on Behalf'
                          : 'Approve on Behalf'}
                      </Button>
                    </Tooltip>
                  )}
              </>
            )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={employeeStatusFilter === 'active'}
              onChange={(e) => {
                setEmployeeStatusFilter(
                  e.target.checked ? 'active' : 'inactive'
                );
              }}
              color="primary"
            />
          }
          label={
            <Typography variant="body1">
              {employeeStatusFilter === 'active'
                ? 'Active Employees'
                : 'Inactive Employees'}
            </Typography>
          }
        />
      </Box>
      <Box sx={{ width: '100%', height: '80vh', paddingTop: 2 }}>
        <DataGrid
          getRowId={(row) => row.identifier}
          columns={columns}
          rows={dataRows}
          getRowHeight={() => 'auto'}
          components={{ Toolbar: GridToolbar }}
          experimentalFeatures={{ columnGrouping: true }}
          columnGroupingModel={columnGroupingModel}
          initialState={{
            sorting: {
              sortModel: [{ field: 'foreman_name', sort: 'asc' }],
            },
          }}
          showCellRightBorder={true}
          showColumnRightBorder={true}
          componentsProps={{
            toolbar: {
              csvOptions: {
                getRowsToExport: getFilteredRows,
                allColumns: true,
                fileName: `${weekEnd} Weekly Timecard Data`,
                fields: columns
                  .filter(
                    (col) =>
                      col.field !== 'location_name' && col.field !== 'status'
                  )
                  .map((col) => col.field),
                delimiter: ',',
              },
            },
          }}
          columnVisibilityModel={{
            weekEnding: false,
            last_status_change_date: employeeStatusFilter === 'inactive',
            coins_empl: false,
            union: true,
            project_name: summaryType === 'dailySummary' ? false : true,
            project_number: summaryType === 'dailySummary' ? false : true,
            package_name: summaryType === 'packSummary' ? true : false,
            activity_name: summaryType === 'packSummary' ? true : false,
            location_type: summaryType === 'dailySummary' ? false : true,
            phase: summaryType === 'dailySummary' ? false : true,
            cost_code: summaryType === 'dailySummary' ? false : true,
            shift: summaryType === 'packSummary' ? false : true,
            straight_time: false,
            st: summaryType === 'timecard' ? false : true,
            overtime: false,
            ot: summaryType === 'timecard' ? false : true,
            doubletime: false,
            dt: summaryType === 'timecard' ? false : true,
          }}
          disableDensitySelector={true}
          disableColumnSelector={true}
        />
      </Box>
      <TimecardConfirmationModal
        open={modalOpen}
        onClose={handleModalClose}
        onApprove={handleApproveWeek}
        weekEndingDate={weekEnd}
        summaryData={calculateSummary()}
      />
      <TimecardUnapprovalModal
        open={unapprovalModalOpen}
        onClose={handleUnapprovalModalClose}
        onUnapprove={handleUnapproveWeek}
        weekEndingDate={weekEnd}
      />
      <TimecardAdminApprovalModal
        open={adminApprovalModalOpen}
        onClose={handleAdminApprovalModalClose}
        onApprove={handleAdminApproveOnBehalf}
        weekEndingDate={weekEnd}
        foremanName={getSelectedForemanName()}
        summaryData={calculateSummary()}
      />
      {/* Admin Unapproval Modal - uses the same component with foremanName for on-behalf text */}
      <TimecardUnapprovalModal
        open={adminUnapprovalModalOpen}
        onClose={handleAdminUnapprovalModalClose}
        onUnapprove={handleAdminUnapproveOnBehalf}
        weekEndingDate={weekEnd}
        foremanName={getSelectedForemanName()}
      />
    </Box>
  );
};
